import { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { Box } from '@material-ui/core';
import _, { isString } from 'lodash';
import { Triangle } from '../page/Admission/components/Triangle';
import aesGcmUtil from './aesGcmUtil';

function maskChineseName(name) {
  if (!isString(name)) {
    return '';
  }
  // 把名字拆分成单个字符
  const nameArray = name.split('');
  // 用 '*' 替换姓氏以外的字符
  for (let i = 1; i < nameArray.length; i++) {
    nameArray[i] = '*';
  }
  // 重新组合成字符串
  return nameArray.join('');
}

function maskEnglishName(name) {
  if (!isString(name)) {
    return '';
  }
  // 把名字拆分成单个单词
  const nameParts = name.split(' ');
  // 对每个单词进行掩码处理
  const maskedParts = nameParts.map((part, index) => {
    // 如果单词长度大于1，则保留第一个字母，其余用 '*' 替换
    if (part.length > 1 && index > 0) {
      return part[0] + '*'.repeat(part.length - 1);
    }
    // 否则直接返回单字母单词
    return part;
  });
  // 重新组合成字符串
  return maskedParts.join(' ');
}

export { maskChineseName, maskEnglishName };

const useGetName = ({ chiName, engName, loginFlag = true, returnFormat }) => {
  const [newchiName, setNewchiName] = useState('');
  const [newengName, setNewengName] = useState('');

  const getName = useCallback(async (decryptName, setName) => {
    try {
      if (decryptName) {
        const resultName = await aesGcmUtil.decryptMessage(decryptName, 1234567890123456);
        setName(resultName);
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const safeSetState = (value, setter) => {
      if (isMounted) {
        setter(value);
      }
    };

    getName(chiName, (name) => safeSetState(name, setNewchiName));
    getName(engName, (name) => safeSetState(name, setNewengName));

    return () => {
      isMounted = false;
    };
  }, [chiName, engName, getName]);

  const fullName = loginFlag
    ? `${newchiName}${newchiName ? ' ' : ''}${newengName}`
    : `${newchiName ? `${maskChineseName(newchiName)}${' '}` : ''}${maskEnglishName(newengName)}`;

  if (returnFormat === 'obj') {
    return {
      nameZh: newchiName,
      nameEn: newengName,
      fullName,
    };
  }
  return fullName;
};

const useGetHkid = (id) => {
  const [hkid, setHkid] = useState('');

  const getId = async () => {
    try {
      if (id) {
        const resultName = await aesGcmUtil.decryptMessage(id, 1234567890123456);
        setHkid(resultName);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getId();
  }, [id]);

  return hkid;
};

const getMdroAlertColor = (key) => {
  switch (key) {
    case 'CPE':
      return {
        bgcolor: '#FFF2C1',
        color: '#7F6B06',
      };
    case 'MPSA':
    case 'VISA':
    case 'VRE':
      return {
        bgcolor: '#FAE5E5',
        color: '#B92B21',
      };

    default:
      return {
        bgcolor: '',
        color: '',
      };
  }
};

const priorityOptions = [
  {
    text: '',
    value: '',
  },
  {
    text: '1',
    value: 1,
  },
  {
    text: '2',
    value: 2,
  },
  {
    text: '3',
    value: 3,
  },
  {
    text: '4',
    value: 4,
  },
];

const getPriorityInfo = (priority) => {
  switch (priority) {
    case 1:
      return {
        color: '#951B07',
        number: 1,
        width: '1.5rem',
        height: '1.3rem',
        fontSize: '0.75rem',
        iconSize: '1.5rem',
      };
    case 2:
      return {
        color: '#CA240A',
        number: 2,
        width: '1.5rem',
        height: '1.3rem',
        fontSize: '0.75rem',
        iconSize: '1.5rem',
      };
    case 3:
      return {
        color: '#F57460',
        number: 3,
        width: '1.5rem',
        height: '1.3rem',
        fontSize: '0.75rem',
        iconSize: '1.5rem',
      };
    case 4:
      return {
        color: '#FFADA0',
        number: 4,
        width: '1.5rem',
        height: '1.3rem',
        fontSize: '0.75rem',
        iconSize: '1.5rem',
      };
    case 0:
      return {
        width: '2.55rem',
        height: '2.24rem',
        fontSize: '1.375rem',
        color: '#FFF',
        number: 'NA',
      };

    default:
      return {};
  }
};

const renderPriorityOption = ({ value, height = '1.3rem' }) => {
  return value ? <Triangle {...getPriorityInfo(value)} height={height} /> : <Box height={height} />;
};

const busStopDateFormat = 'DD MMM HH:mm';

const mathBuffTime = (currentTime, lastTime) => {
  if (currentTime !== 'Invalid date' && lastTime !== 'Invalid date' && lastTime && currentTime) {
    if (moment(currentTime).isBefore(moment(lastTime))) {
      return '+NA';
    }
    const diffMinutes = moment(currentTime).diff(moment(lastTime), 'minutes');
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    // 格式化为（小时:分钟）
    const formattedDiff = `${hours}:${minutes < 10 ? '0' : ''}${minutes}`;

    return `+${formattedDiff}`;
  } else {
    return null;
  }
};

const eAdmissionFormBtnEnum = {
  Withdraw: {
    text: 'Withdraw',
    backgroundColor: '#787272',
    type: 'fill',
  },
  Hold: {
    text: 'Hold',
    backgroundColor: '#8896AE',
    type: 'fill',
  },
  'Cancel holding': {
    text: 'Cancel holding',
    backgroundColor: '#FF655C',
    type: 'fill',
  },
  Save: {
    text: 'Save',
    backgroundColor: '#4C93FF',
    type: 'fill',
  },
  Cancel: {
    text: 'Cancel',
    backgroundColor: '#F5FAFF',
    type: 'default',
  },
  'Confirm Adm. Ready': {
    text: 'Confirm Adm. Ready',
    backgroundColor: '#28D2AB',
    type: 'fill',
  },
  'Assign Ward': {
    text: 'Assign Ward',
    backgroundColor: '#28D2AB',
    type: 'fill',
  },
  'Cancel Assignd Ward & Re-assign': {
    text: 'Cancel Assignd Ward & Re-assign',
    backgroundColor: '#FF655C',
    type: 'fill',
  },
  Redirect: {
    text: 'Redirect',
    backgroundColor: '#FF655C',
    type: 'fill',
  },
  Acknowledge: {
    text: 'Acknowledge',
    backgroundColor: '#28D2AB',
    type: 'fill',
  },
  'Ward Ready': {
    text: 'Ward Ready',
    backgroundColor: '#28D2AB',
    type: 'fill',
  },
  'ACK. and ward ready': {
    text: 'ACK. and ward ready',
    backgroundColor: '#4C93FF',
    type: 'fill',
  },
};

const actionTypeEnum = {
  Withdraw: 1,
  Hold: 2,
  'Cancel holding': 3,
  Save: 4,
  'Confirm Adm. Ready': 5,
  'Assign Ward': 6,
  'Cancel Assignd Ward & Re-assign': 7,
  Acknowledge: 8,
  'Ward Ready': 9,
  Redirect: 10,
  'ACK. and ward ready': 13,
};

const secondsToHHMM = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours < 0 || minutes < 0) {
    return 'NA';
  }

  const formattedTime = `${hours}:${minutes < 10 ? '0' : ''}${minutes}`;
  return formattedTime;
};

const getAssignmentBtn = (btn) => {
  switch (btn) {
    case 'Assign':
      return {
        text: 'Assign',
        backgroundColor: '#28D2AB',
        type: 'fill',
        width: '12.5rem',
      };
    case 'Re-assign to next ward':
      return {
        text: 'Re-assign to next ward',
        backgroundColor: '#FF655C',
        type: 'fill',
        width: '21rem',
      };
    case 'Save':
      return {
        text: 'Save',
        backgroundColor: '#4C93FF',
        type: 'fill',
        width: '12.5rem',
      };
    case 'Cancel':
      return {
        text: 'Cancel',
        backgroundColor: '#F5FAFF',
        type: 'default',
        width: '12.5rem',
      };

    default:
      return {
        text: btn,
        backgroundColor: '#FF655C',
        type: 'fill',
        width: '21rem',
      };
  }
};

const initThreshold = (str) => {
  return str?.replace(/%/g, '') ? `${str?.replace(/%/g, '')}%` : '';
};

// 生成新的栅格床位
export function getEADBeds(row, column) {
  const grid = [];

  for (let r = 0; r < row; r++) {
    for (let c = 0; c < column; c++) {
      const cell = {
        row: r + 1,
        column: c + 1,
        key: `(${r + 1},${c + 1})`,
        bedName: '',
      };
      grid.push(cell);
    }
  }

  return grid;
}

const isFirstPageRequest = (params) => {
  // 判断当前请求是否是第一页或不分页请求
  return params?.pageNo === 1 || params?.pageNo === null || typeof params?.pageNo === 'undefined';
};

export const handleAdmissionFormRedirect = ({
  admissionList = [],
  forceClear = false,
  params,
  pendingBarcodeAeNoRef,
  redirectCallBack = null,
  isIpad = false,
  isLogin,
}) => {
  // 处理扫码筛选命中后的 admissionForm 跳转
  const pendingBarcodeAeNo = pendingBarcodeAeNoRef?.current;

  // 未登录时只保留输入框筛选效果，不跳转 admissionForm
  if (!isLogin) {
    if (pendingBarcodeAeNo && params?.filter?.aeNo === pendingBarcodeAeNo) {
      pendingBarcodeAeNoRef.current = '';
    }
    return false;
  }

  // 如果是ipad的时候就扫完码不跳转页面，只填充到输入框进行筛选
  if (isIpad) {
    return false;
  }

  if (!pendingBarcodeAeNo || params?.filter?.aeNo !== pendingBarcodeAeNo) {
    return false;
  }
  const targetAdmission = _.find(admissionList, (item) => item?.caseNo === pendingBarcodeAeNo) ?? null;

  if (targetAdmission && _.isFunction(redirectCallBack)) {
    pendingBarcodeAeNoRef.current = '';
    redirectCallBack(targetAdmission?.eaId, targetAdmission?.transferRequestId);

    return true;
  }

  if (forceClear || isFirstPageRequest(params)) {
    pendingBarcodeAeNoRef.current = '';
  }

  return false;
};

export const parseCubiclePosition = (position) => {
  if (_.isObject(position)) {
    return position;
  }
  if (!position) {
    return { X: 0, Y: 0 };
  }
  return JSON.parse(position);
};

export const normalizeLayoutNumber = (value, fallback = 0) => {
  const numberValue = parseFloat(value);
  return Number.isNaN(numberValue) ? fallback : numberValue;
};

export const normalizeNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return value;
  }

  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? value : numberValue;
};

export const buildAEDBedList = ({ beds, cubicleRows, cubicleCols, editMode = false, fillEmptyBeds = true }) => {
  const rowCount = normalizeLayoutNumber(cubicleRows);
  const columnCount = normalizeLayoutNumber(cubicleCols);
  const bedMap = _.keyBy(
    _.map(beds, (bedItem) => {
      const row = normalizeNumber(bedItem?.cubRow);
      const column = normalizeNumber(bedItem?.cubColumn);

      let bedObj = {
        key: `(${row},${column})`,
        column,
        row,
        bedName: bedItem?.bedName,
      };
      if (editMode) {
        bedObj.isOccupied = bedItem?.isOccupied;
      } else {
        bedObj.patientInfo = bedItem?.pat;
      }

      return bedObj;
    }),
    'key'
  );
  const result = [];

  if (!fillEmptyBeds) {
    return _.values(bedMap);
  }

  // 后端会过滤空床，这里按行列补齐完整床位矩阵。
  for (let row = 1; row <= rowCount; row++) {
    for (let column = 1; column <= columnCount; column++) {
      const key = `(${row},${column})`;
      result.push(bedMap[key] ?? { column, row, bedName: '', key, isOccupied: false });
    }
  }

  return result;
};

export const initStartDate = (outstanding) => {
  if (outstanding?.noDate || outstanding?.startTime === 'Invalid date') {
    return null;
  }
  return outstanding?.startTime
    ? moment(outstanding?.startTime)
    : moment(moment().subtract(1, 'days').format('yyyy-MM-DD 00:00:00').valueOf());
};

export const initEndDate = (outstanding) => {
  if (outstanding?.noDate || outstanding?.stopTime === 'Invalid date') {
    return null;
  }
  return outstanding?.stopTime
    ? moment(outstanding?.stopTime)
    : moment(moment().add(1, 'days').format('yyyy-MM-DD 23:59:59').valueOf());
};

export default {
  useGetName,
  useGetHkid,
  getMdroAlertColor,
  priorityOptions,
  renderPriorityOption,
  busStopDateFormat,
  mathBuffTime,
  eAdmissionFormBtnEnum,
  actionTypeEnum,
  secondsToHHMM,
  getAssignmentBtn,
  initThreshold,
  getEADBeds,
};
